import React, { useRef, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ImagePickerDialog from "../ImagePickerDialog";

const countDrops = (text) => (String(text || "").match(/\[drop\]/g) || []).length;

const splitOptionsText = (text) =>
    String(text || "")
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean);

const insertAtCursor = (input, value, text) => {
    if (!input) return (value || "") + text;

    const start = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? value.length;

    return value.slice(0, start) + text + value.slice(end);
};

const normalizeDropdowns = (dropdowns, amount) => {
    const source = Array.isArray(dropdowns) ? dropdowns : [];

    return Array.from({ length: amount }, (_, idx) => {
        const dropdown = source[idx] || {};
        const options = Array.isArray(dropdown.options) ? dropdown.options.map((item) => String(item)) : [];

        return {
            options,
            correctAnswer: String(dropdown.correctAnswer || options[0] || ""),
        };
    });
};

const QuestionFormDropdown2 = ({ value, onChange }) => {
    const v = value || {};
    const dropAmount = countDrops(v.question);
    const dropdowns = normalizeDropdowns(v.dropdowns, dropAmount);
    const [helpOpen, setHelpOpen] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const questionRef = useRef(null);

    function emit(patch) {
        onChange({
            ...v,
            type: "dropdown2",
            ...patch,
        });
    }

    function setQuestion(question) {
        const nextAmount = countDrops(question);
        emit({
            question,
            dropdowns: normalizeDropdowns(v.dropdowns, nextAmount),
        });
    }

    function appendDrop() {
        setQuestion(`${v.question || ""}[drop]`);
    }

    function onPickImage(img) {
        const caption = img.originalFilename || "Зображення";
        const block =
            `\n<figure style="text-align:center;">` +
            `\n  <img src="${img.url}" style="width:100%; max-width:100%; height:auto;" alt="${caption}" />` +
            `\n  <figcaption class="imgcaption">${caption}</figcaption>` +
            `\n</figure>\n`;
        const next = insertAtCursor(questionRef.current, v.question || "", block);

        setQuestion(next);
        setPickerOpen(false);
    }

    function setDropdown(idx, patch) {
        emit({
            dropdowns: dropdowns.map((dropdown, dropdownIdx) => (
                dropdownIdx === idx ? { ...dropdown, ...patch } : dropdown
            )),
        });
    }

    return (
        <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                <Typography variant="subtitle2">Dropdown 2.0</Typography>
                <Tooltip title="Показати інструкцію для налаштування цього завдання">
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<HelpOutlineIcon />}
                        onClick={() => setHelpOpen(true)}
                    >
                        Як користуватися
                    </Button>
                </Tooltip>
            </Stack>

            <TextField
                label="Підзаголовок"
                value={v.subtitle || ""}
                onChange={(e) => emit({ subtitle: e.target.value })}
                helperText="Необов'язково. Показується під назвою модуля."
                fullWidth
            />

            <TextField
                label="Текст завдання"
                value={v.question || ""}
                onChange={(e) => setQuestion(e.target.value)}
                helperText="Вставляйте [drop] у місцях, де учень має обрати відповідь зі списку. Можна використовувати HTML: <strong>, <em>, <img>, <iframe>, <video>."
                fullWidth
                multiline
                minRows={8}
                inputRef={questionRef}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button variant="outlined" onClick={appendDrop}>
                    Додати [drop]
                </Button>
                <Button variant="outlined" onClick={() => setPickerOpen(true)}>
                    Вставити зображення
                </Button>
            </Stack>

            <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Списки: {dropAmount}
                </Typography>

                {dropAmount === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Додайте хоча б один [drop] у тексті завдання.
                    </Typography>
                ) : (
                    <Stack spacing={2}>
                        {dropdowns.map((dropdown, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    p: 2,
                                }}
                            >
                                <Stack spacing={1.5}>
                                    <Typography variant="subtitle2">Список #{idx + 1}</Typography>
                                    <TextField
                                        label="Варіанти відповідей"
                                        value={dropdown.options.join("\n")}
                                        onChange={(e) => {
                                            const options = splitOptionsText(e.target.value);
                                            setDropdown(idx, {
                                                options,
                                                correctAnswer: options.includes(dropdown.correctAnswer)
                                                    ? dropdown.correctAnswer
                                                    : options[0] || "",
                                            });
                                        }}
                                        helperText="Один варіант на рядок."
                                        fullWidth
                                        multiline
                                        minRows={4}
                                    />
                                    <TextField
                                        label="Правильна відповідь"
                                        value={dropdown.correctAnswer}
                                        onChange={(e) => setDropdown(idx, { correctAnswer: e.target.value })}
                                        helperText="Має точно збігатися з одним із варіантів."
                                        fullWidth
                                    />
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>

            <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Як налаштувати Dropdown 2.0</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                        <Typography variant="body2">
                            1. У полі "Текст завдання" напишіть текст, який побачить учень.
                        </Typography>
                        <Typography variant="body2">
                            2. У місцях, де має бути випадаючий список, вставляйте [drop].
                        </Typography>
                        <Typography variant="body2">
                            3. Для кожного [drop] нижче з'явиться блок "Список". Додайте варіанти відповідей:
                            один варіант на один рядок.
                        </Typography>
                        <Typography variant="body2">
                            4. Правильна відповідь має точно збігатися з одним із варіантів списку.
                        </Typography>
                        <Typography variant="body2">
                            5. Відповіді перевіряються у тому самому порядку, у якому [drop] стоять у тексті.
                        </Typography>
                        <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap", m: 0 }}>
{`Приклад:
1) Як тебе не любити [drop] мій
2) Ім'я поета Шевченка - [drop]`}
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHelpOpen(false)}>Закрити</Button>
                </DialogActions>
            </Dialog>

            <ImagePickerDialog
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onPick={onPickImage}
            />
        </Stack>
    );
};

export default QuestionFormDropdown2;
